import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@designs-by-maryam/ui/components/accordion";
import { useQuery } from "@tanstack/react-query";
import { HelpCircle } from "lucide-react";
import { motion } from "motion/react";
import { useMemo } from "react";
import { FAQ_SECTION, FAQ_ITEMS as STATIC_FAQ_ITEMS } from "@/data/faq-content";
import { getStaggerDelay, useSafeMotion } from "@/lib/motion";
import { faqQueryOptions } from "@/lib/query-options";

export function FAQSection() {
	const { shouldReduce, slideUp, viewportOnce } = useSafeMotion();

	// Fetch FAQs from CMS - may be prefetched in route loader
	const { data: cmsFaqs } = useQuery({
		...faqQueryOptions(),
		staleTime: 30 * 60 * 1000, // 30 minutes
	});

	// Transform CMS FAQs to component format with static fallback
	const faqItems = useMemo(() => {
		if (cmsFaqs?.data && cmsFaqs.data.length > 0) {
			return cmsFaqs.data.map((faq) => ({
				id: faq.id,
				question: faq.question,
				answer: faq.answer,
			}));
		}
		return STATIC_FAQ_ITEMS;
	}, [cmsFaqs]);

	return (
		<motion.section
			className="space-y-6"
			initial={slideUp.initial}
			transition={slideUp.transition}
			viewport={viewportOnce}
			whileInView={slideUp.animate}
		>
			<motion.div
				className="space-y-3 text-center"
				initial={{ opacity: 0, y: shouldReduce ? 0 : 10 }}
				transition={{
					duration: shouldReduce ? 0.01 : 0.4,
					delay: shouldReduce ? 0 : 0.1,
				}}
				viewport={{ once: true }}
				whileInView={{ opacity: 1, y: 0 }}
			>
				<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary">
					<HelpCircle className="h-6 w-6" />
				</div>
				<h2 className="font-semibold text-2xl tracking-tight">
					{FAQ_SECTION.title}
				</h2>
				<p className="text-muted-foreground">{FAQ_SECTION.description}</p>
			</motion.div>

			<Accordion className="w-full space-y-3" collapsible type="single">
				{faqItems.map((item, index) => (
					<motion.div
						initial={{ opacity: 0, x: shouldReduce ? 0 : -20 }}
						key={item.id}
						transition={{
							duration: shouldReduce ? 0.01 : 0.3,
							delay: getStaggerDelay(index, 0.2, 0.05, shouldReduce),
						}}
						viewport={{ once: true }}
						whileInView={{ opacity: 1, x: 0 }}
					>
						<AccordionItem
							className="overflow-hidden rounded-lg border bg-card transition-all hover:border-primary/20 hover:shadow-md dark:hover:border-primary/30"
							value={item.id}
						>
							<AccordionTrigger className="px-6 py-4 text-left transition-all hover:text-primary [&[data-state=open]]:bg-primary/5 dark:[&[data-state=open]]:bg-primary/10">
								<span className="flex items-start gap-3">
									<span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary text-xs dark:bg-primary/20 dark:text-primary">
										{index + 1}
									</span>
									<span className="font-medium">{item.question}</span>
								</span>
							</AccordionTrigger>
							<AccordionContent className="px-6 pb-4 pl-[60px] text-muted-foreground">
								{item.answer}
							</AccordionContent>
						</AccordionItem>
					</motion.div>
				))}
			</Accordion>
		</motion.section>
	);
}
